import { Component, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CategorySelection, CodeLookup, EmployeeAPIModel, TaskTypeAPIModel } from 'src/app/models/api.model'
import { CommonService } from 'src/app/services/common/common.service'
import countryCodes from '../../../../assets/other/country-code.json'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { OfflineTrackerService } from 'src/app/services/offline-tracker/offline-tracker.service'
import { getEmployeeFromState } from 'src/app/store/selector/employee.selector'
import { Store } from '@ngrx/store'
import { EmployeeService } from 'src/app/services/employee/employee.service'
import { AppState } from 'src/app/store/state/app.state'
import {
  ACKNOWLEDGE_SHARED_STATUS,
  RECEIVED_DOCUMENTS,
  NATURE_OF_ISSUE,
  RECEIVED_MODE,
  STATUS,
  MOBILE_COUNTRY_CODE,
  MOBILE_NUMBER,
  EMPLOYEE_STATUS,
  EMPLOYEE_CODE,
  EMAIL_ID
} from 'src/app/constants/field-id-constants'
import { getOptionsFromState } from 'src/app/store/selector/initialize.selector'
import { validatePFNumber, validateUAN } from '../../import/validators/validator.functions'
import { emailValidatorForNonMandatory, futureDateValidator } from 'src/app/validators/validators.validator'
import { filter } from 'rxjs'

@Component({
  selector: 'app-create-new-offline-tracker',
  templateUrl: './create-new-offline-tracker.component.html',
  styleUrls: ['./create-new-offline-tracker.component.scss']
})
export class CreateNewOfflineTrackerComponent implements OnInit {
  createTrackerForm: FormGroup
  isAcknowledgeSharedStatusMandatory: boolean = false
  selectedEmployeeCompanyEntityId: string = null
  countryCodes: { dialCode: string }[] = countryCodes
  acknowledgeShareStatusOptionsList: CodeLookup[] = []
  requestCompletionStatusOptionsList: CodeLookup[] = []
  receivedModeOptionsList: CodeLookup[] = []
  documentsReceivedOptionsList: CodeLookup[] = []
  natureOfIssueOptionsList: CodeLookup[] = []
  employeeStatusOptionsList: CodeLookup[] = []
  selectTaskTypesSubscription$: any
  offlineTrackerTaskTypes: TaskTypeAPIModel[]
  employeeDataSubscription: any
  isEmployeeCodeMandatory: boolean = false
  isEmailIdMandatory: boolean = false
  selectedTaskType: TaskTypeAPIModel
  private readonly emailIdValidators = [
    emailValidatorForNonMandatory(),
    Validators.minLength(3),
    Validators.maxLength(70)
  ]
  private readonly employeeCodeValidators = [Validators.minLength(3), Validators.maxLength(40)]
  protected readonly module = GlobalConstants.MODULE.OFFLINE_TRACKER
  readonly NAME_CONTROL_NAME = 'name'
  readonly TASK_CATEGORY_CONTROL_NAME = 'taskCategory'
  readonly TASK_SUB_CATEGORY_CONTROL_NAME = 'taskSubCategory'

  constructor(
    private fb: FormBuilder,
    private cs: CommonService,
    private initializeService: InitializeService,
    private offlineTrackerService: OfflineTrackerService,
    private employeeService: EmployeeService,
    private appState: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.createForm()
    this.populateOptions()
  }

  createForm(): void {
    this.createTrackerForm = this.fb.group({
      companyEntityId: ['', Validators.required],
      [EMPLOYEE_CODE]: ['', this.employeeCodeValidators],
      [EMPLOYEE_STATUS]: ['', Validators.required],
      firstName: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern('^[a-zA-Z ]*$')]
      ],
      middleName: ['', [Validators.minLength(3), Validators.maxLength(100), Validators.pattern('^[a-zA-Z ]*$')]],
      lastName: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern('^[a-zA-Z ]*$')]
      ],
      uanNumber: [
        '',
        [Validators.minLength(11), Validators.maxLength(12), Validators.required, Validators.pattern('^[0-9]*$')]
      ],
      memberId: [
        '',
        [Validators.required, Validators.minLength(21), Validators.maxLength(21), Validators.pattern('^[a-zA-Z0-9]+$')]
      ],
      [NATURE_OF_ISSUE]: this.fb.array([], [Validators.required, Validators.minLength(1)]),
      additionalDetails: ['', [Validators.minLength(3), Validators.maxLength(100)]],
      [RECEIVED_DOCUMENTS]: this.fb.array([], [Validators.required, Validators.minLength(1)]),
      [MOBILE_COUNTRY_CODE]: ['+91', [Validators.maxLength(4)]],
      [MOBILE_NUMBER]: ['', [Validators.minLength(10), Validators.maxLength(10), Validators.pattern('^[0-9]{10}$')]],
      [EMAIL_ID]: ['', this.emailIdValidators],
      [RECEIVED_MODE]: this.fb.array([], [Validators.required, Validators.minLength(1)]),
      receivedDate: ['', futureDateValidator()],
      receivedMonth: [''],
      [STATUS]: ['', Validators.required],
      statusDate: ['', futureDateValidator()],
      [ACKNOWLEDGE_SHARED_STATUS]: [''],
      requestCompletionStatus: ['', Validators.required],
      remarks: ['', [(Validators.minLength(3), Validators.maxLength(300))]],
      [this.TASK_CATEGORY_CONTROL_NAME]: ['', [Validators.required]],
      [this.TASK_SUB_CATEGORY_CONTROL_NAME]: ['', [Validators.required]],
      [this.NAME_CONTROL_NAME]: ['', [Validators.required]]
    })

    this.handleFormChange()
  }

  handleFormChange(): void {
    this.createTrackerForm.get(STATUS)?.valueChanges.subscribe(value => {
      this.isAcknowledgeSharedStatusMandatory = ['SUBMITTED', 'ONLINE_COMPLETED'].includes(value)

      if (this.isAcknowledgeSharedStatusMandatory) {
        this.createTrackerForm.get(ACKNOWLEDGE_SHARED_STATUS)?.setValidators(Validators.required)
      } else {
        this.createTrackerForm.get(ACKNOWLEDGE_SHARED_STATUS)?.clearValidators()
      }
      this.createTrackerForm.get(ACKNOWLEDGE_SHARED_STATUS)?.updateValueAndValidity()
    })

    this.createTrackerForm.get(MOBILE_NUMBER)?.valueChanges.subscribe(value => {
      if (value) {
        this.createTrackerForm.get(MOBILE_COUNTRY_CODE)?.setValidators(Validators.required)
      } else {
        this.createTrackerForm.get(MOBILE_COUNTRY_CODE)?.clearValidators()
      }
      this.createTrackerForm.get(MOBILE_COUNTRY_CODE)?.updateValueAndValidity()
    })

    this.createTrackerForm
      .get(EMPLOYEE_STATUS)
      ?.valueChanges.pipe(filter(value => !!value))
      .subscribe(value => {
        const employeeCodeControl = this.createTrackerForm.get(EMPLOYEE_CODE)
        const emailIdControl = this.createTrackerForm.get(EMAIL_ID)
        if (value === 'ACTIVE') {
          employeeCodeControl.clearValidators()
          employeeCodeControl.setValidators([...this.employeeCodeValidators, Validators.required])

          emailIdControl.clearValidators()
          this.createTrackerForm.get(EMAIL_ID).setValidators(this.emailIdValidators)

          this.isEmployeeCodeMandatory = true
          this.isEmailIdMandatory = false
        } else {
          employeeCodeControl.clearValidators()
          employeeCodeControl.setValidators(this.employeeCodeValidators)

          emailIdControl.clearValidators()
          emailIdControl?.setValidators([...this.emailIdValidators, Validators.required])

          this.isEmployeeCodeMandatory = false
          this.isEmailIdMandatory = true
        }
        emailIdControl.updateValueAndValidity()
        employeeCodeControl.updateValueAndValidity()
      })
  }

  callSaveAPI(): void {
    const formData = {
      ...this.createTrackerForm.value,
      taskCode: this.selectedTaskType.code
    }
    if (formData.mobileNumber) {
      formData.mobileNumber = formData.mobileCountryCode.slice(1) + ' ' + formData.mobileNumber
    }

    this.offlineTrackerService.dispatchSaveAction(
      formData,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.OFFLINE_TRACKER, GlobalConstants.ROUTING.SUMMARY])
    )
  }

  get f() {
    return this.createTrackerForm.controls
  }

  saveAndExit(): void {
    if (this.createTrackerForm.invalid) {
      this.createTrackerForm.markAllAsTouched()
      return
    }
    this.callSaveAPI()
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.OFFLINE_TRACKER, GlobalConstants.ROUTING.SUMMARY)
  }

  populateOptions() {
    this.appState.select(getOptionsFromState).subscribe(options => {
      if (options) {
        this.acknowledgeShareStatusOptionsList = options[GlobalConstants.OPTION_TYPE.ACKNOWLEDGE_SHARE_STATUS]
        this.requestCompletionStatusOptionsList = options[GlobalConstants.OPTION_TYPE.REQUEST_COMPLETION_STATUS]
        this.receivedModeOptionsList = options[GlobalConstants.OPTION_TYPE.RECEIVED_MODE]
        this.documentsReceivedOptionsList = options[GlobalConstants.OPTION_TYPE.RECEIVED_DOCUMENTS]
        this.natureOfIssueOptionsList = options[GlobalConstants.OPTION_TYPE.NATURE_OF_ISSUE]
        this.employeeStatusOptionsList = options[GlobalConstants.OPTION_TYPE.EMPLOYEE_STATUS]
      }
    })
  }

  handleCompanySelectedValue(companyEntityId: string) {
    this.createTrackerForm.patchValue({ companyEntityId })
  }

  handleEmployeeValueChange(employeeCode: string) {
    this.createTrackerForm.patchValue({ employeeCode })
  }

  handleEmployeeSelected(employee: EmployeeAPIModel) {
    if (employee) {
      this.selectedEmployeeCompanyEntityId = employee?.companyEntityId
      this.createTrackerForm.patchValue({ employeeCode: employee?.employeeCode })
      this.getEmployeeDetails(employee.id)
    }
  }

  onMultiSelectChange(value: { key1: string; key2: string }[], field: string) {
    const formArray = this.createTrackerForm.get(field) as FormArray
    formArray.clear()
    value.forEach(v => formArray.push(this.fb.control(v.key2)))
  }

  onNewOptionAdded(newOption: CodeLookup, field: string) {
    if (field === NATURE_OF_ISSUE) {
      this.natureOfIssueOptionsList.push(newOption)
    }
    if (field === RECEIVED_MODE) {
      this.receivedModeOptionsList.push(newOption)
    }
    if (field === RECEIVED_DOCUMENTS) {
      this.documentsReceivedOptionsList.push(newOption)
    }
  }

  validateUAN(event: any) {
    validateUAN(event)
  }

  validatePFNumber(event: any) {
    validatePFNumber(event)
  }


  private getEmployeeDetails(id: string) {
    this.employeeService.dispatchGetEmployeeWithReferenceDetails(id)
    this.employeeDataSubscription = this.appState.select(getEmployeeFromState).subscribe(employee => {
      const statutoryDetails = employee?.employeeStatutoryDetails
      const uanNumber = statutoryDetails?.uanNumber
      const pfNumber = statutoryDetails?.currentPFAccountNumber
      this.createTrackerForm.patchValue({
        uanNumber,
        memberId: pfNumber,
        firstName: employee?.firstName,
        middleName: employee?.middleName,
        lastName: employee?.lastName
      })
    })
  }

  private getOfflineTrackerNameAndType(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.OFFLINE_TRACKER, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.offlineTrackerTaskTypes = taskTypes
        }
      })
  }

  onCategorySelected(selectedCategorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = selectedCategorySelection
    this.createTrackerForm.get(this.NAME_CONTROL_NAME).setValue('')
    this.createTrackerForm.get(this.TASK_CATEGORY_CONTROL_NAME).setValue(selectedCategory)
    this.createTrackerForm.get(this.TASK_SUB_CATEGORY_CONTROL_NAME).setValue(selectedSubCategory)
    this.getOfflineTrackerNameAndType(selectedCategorySelection)
  }

  onTaskNameSelectChange(selectedValue: any) {
    this.selectedTaskType = this.offlineTrackerTaskTypes.find(taskType => taskType.code === selectedValue)
  }

}
